import useSWR from 'swr';
import { useSWRList } from '@hooks/use-swr-list';
import { useCallback, useMemo } from 'react';
import { useSamplerCartContext } from '@web/onboarding/SamplerBoxFlowContainer/SamplerCartProvider';

const getPlanTypesUrl = (cartId, petId) => {
  if (!cartId || !petId) return null;
  return `/api/v4/onboarding/${cartId}/pets/${petId}/plans/`;
};

const createMealPlanCombinationsUrl = (cartId, petId, mealplanId, cadence) => {
  return `/api/v4/onboarding/${cartId}/pets/${petId}/plans/${mealplanId}/?cadence=${cadence}`;
};

// Sampler box only supports fresh
const PLAN_TYPE_KEYS = ['fresh'];

export const useSamplerPlanCombinations = petId => {
  const { cartId } = useSamplerCartContext();

  // Fetch plan types directly
  const { data: planTypes, isLoading: isPlanTypesLoading } = useSWR(
    cartId && petId ? getPlanTypesUrl(cartId, petId) : null,
  );

  const filteredPlanTypes = useMemo(() => {
    if (!planTypes?.length) return planTypes;
    return planTypes.filter(pt => PLAN_TYPE_KEYS.includes(pt.plan_type_name));
  }, [planTypes]);

  const { data, error, isValidating, mutate } = useSWRList(
    !!filteredPlanTypes?.length && cartId && petId
      ? filteredPlanTypes.map(planType => {
          const smallestCadence = planType.available_cadences?.[0];
          return createMealPlanCombinationsUrl(
            cartId,
            petId,
            planType.mealplan_id,
            smallestCadence,
          );
        })
      : [],
  );

  const combinationsByPlanType = useMemo(() => {
    const result = PLAN_TYPE_KEYS.reduce((acc, key) => {
      acc[key] = {
        planCombinations: [],
        availableRecipes: { fresh: [], baked: [] },
        cadence: null,
        recommendedCombinationId: null,
        recommendedCombination: null,
      };
      return acc;
    }, {});

    if (data && filteredPlanTypes && Array.isArray(data)) {
      data.forEach((combinations, index) => {
        const planType = filteredPlanTypes[index]?.plan_type_name;
        if (!planType) return;
        result[planType] = {
          planCombinations: combinations?.plan_combinations || [],
          availableRecipes: combinations?.available_recipes || {
            fresh: [],
            baked: [],
          },
          cadence: combinations?.cadence,
          recommendedCombinationId: combinations?.recommended_combination_id,
          recommendedCombination: combinations?.recommended_combination_id
            ? combinations?.plan_combinations?.find(
                plan =>
                  plan.combination_id ===
                  combinations.recommended_combination_id,
              )
            : combinations?.plan_combinations?.[0],
        };
      });
    }
    return result;
  }, [data, filteredPlanTypes]);

  const refreshPlans = useCallback(() => {
    mutate();
  }, [mutate]);
  return {
    combinationsByPlanType,
    error,
    isLoading: (cartId && petId && !data && !error) || isPlanTypesLoading,
    isValidating,
    refreshPlans,
  };
};
